import {
  useState,
  useEffect,
  useCallback,
  useRef,
  HTMLAttributes,
} from 'react';

import Autocomplete, {
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { style } from 'typestyle';

import RevisionAutocompleteInput from './RevisionAutocompleteInput';
import RevisionAutocompleteOption from './RevisionAutocompleteOption';
import { useAppSelector } from '../../hooks/app';
import { fetchRecentRevisions } from '../../logic/treeherder';
import { Strings } from '../../resources/Strings';
import {
  Colors,
  FontsRaw,
  Spacing,
  captionStylesLight,
  captionStylesDark,
} from '../../styles';
import type { Changeset, Repository } from '../../types/state';
import { simpleDebounce } from '../../utils/simple-debounce';

interface Props {
  compact: boolean;
  inputPlaceholder: string;
  displayedRevisions: Changeset[];
  searchType: 'base' | 'new';
  repository: Repository['name'];
  onSearchResultsToggle: (item: Changeset) => void;
  listItemComponent?: 'checkbox' | 'radio';
}

export default function SearchInputAndResults({
  compact,
  inputPlaceholder,
  displayedRevisions,
  searchType,
  repository,
  onSearchResultsToggle,
  listItemComponent,
}: Props) {
  const mode = useAppSelector((state) => state.theme.mode);
  const theme = useTheme();

  const [recentRevisions, setRecentRevisions] = useState(
    null as null | Changeset[],
  );
  const [searchError, setSearchError] = useState(null as null | string);
  const [inputValue, setInputValue] = useState('');

  // The last used searchTerm is kept in a ref, so that it's possible to use it
  // in an effect when the repository prop changes. It's not stored in a state
  // because it's not used for rendering DOM.
  const lastSearchTermRef = useRef('');
  // This is used as a request id. It's incremented for each new request, and
  // is checked when the response comes back, so that an older response is
  // disregarded.
  const requestsCounterRef = useRef(0);

  const containerRef = useRef(null as null | HTMLElement);

  // Styling from original SearchResultsList
  const sharedSelectStyles = {
    borderRadius: '4px',
    marginTop: `${Spacing.xSmall}px`,
    maxHeight: '285px',
    overflow: 'auto',
    maxWidth: '100%',
    padding: `${Spacing.xSmall}px`,
    border: `1px solid ${Colors.BorderDefault}`,
    zIndex: 100,
  };

  const getListStyles = (themeMode: string) => {
    const captionStyle =
      themeMode === 'light' ? captionStylesLight : captionStylesDark;

    return style({
      backgroundColor: theme.palette.searchDropdown.background,
      position: 'relative',
      ...sharedSelectStyles,
      $nest: {
        // Autocomplete option highlighting
        'li[aria-selected="true"]': {
          backgroundColor: `${theme.palette.searchDropdown.hover} !important`,
          borderRadius: '4px',
          paddingTop: `${Spacing.xSmall}px`,
        },
        'li:hover': {
          backgroundColor: `${theme.palette.searchDropdown.hover} !important`,
          borderRadius: '4px',
          paddingTop: `${Spacing.xSmall}px`,
        },
        'li[data-focus="true"]': {
          backgroundColor: `${theme.palette.searchDropdown.hover} !important`,
          borderRadius: '4px',
          paddingTop: `${Spacing.xSmall}px`,
        },
        'li.Mui-focused': {
          backgroundColor: `${theme.palette.searchDropdown.hover} !important`,
          borderRadius: '4px',
          paddingTop: `${Spacing.xSmall}px`,
        },
        // General li styling for all options
        li: {
          paddingTop: `${Spacing.xSmall}px`,
        },
        // Original list item button styles (keeping for compatibility)
        '.MuiListItemButton-root': {
          padding: `${Spacing.xSmall}px ${Spacing.Small}px`,
          $nest: {
            '&:hover': {
              backgroundColor: theme.palette.searchDropdown.hover,
              borderRadius: '4px',
            },
            '&:active': {
              backgroundColor: theme.palette.searchDropdown.active,
              borderRadius: '4px',
            },
          },
        },
        '.item-selected': {
          backgroundColor: theme.palette.searchDropdown.hover,
          borderRadius: '4px',
        },
        '.revision-hash': {
          ...FontsRaw.BodyDefault,
          marginRight: Spacing.Small,
        },
        '.info-caption': {
          ...captionStyle,
        },
        '.MuiTypography-root': {
          ...FontsRaw.BodyDefault,
        },
      },
    });
  };

  const searchRecentRevisions = useCallback(
    async (
      searchTerm: string,
      { autoSelect = false }: { autoSelect?: boolean } = {},
    ) => {
      // If the URL has a parameter useFulltextSearch, use it to determine the search type.
      const urlParams = new URLSearchParams(window.location.search);
      const useFulltextSearch = urlParams.has('useFulltextSearch');

      const isPartialHash = /^(?=.*[a-f])[a-f0-9]{4,39}$/i;
      const isCompleteHash = /^(?=.*[a-f])[a-f0-9]{40}$/i;
      const authorInfoMatch = /[^0-9a-fA-F]/;

      // Reset various states
      setSearchError(null);
      setRecentRevisions(null);
      lastSearchTermRef.current = '';

      // By increasing the counter, we ensure that responses for inflight requests will be ignored.
      const thisRequestId = ++requestsCounterRef.current;

      let searchParameters;

      if (!searchTerm) {
        searchParameters = { repository };
      } else if (searchTerm.length < 3) {
        setSearchError(Strings.errors.warningText);
        return;
      } else if (isCompleteHash.test(searchTerm)) {
        // Prioritize complete hash match
        searchParameters = { repository, hash: searchTerm };
      } else if (useFulltextSearch) {
        searchParameters = isPartialHash.test(searchTerm)
          ? { repository, hash: searchTerm }
          : { repository, search: searchTerm };
      } else {
        // Default to author/hash for partial matches
        searchParameters = authorInfoMatch.test(searchTerm)
          ? { repository, author: searchTerm }
          : { repository, hash: searchTerm };
      }

      lastSearchTermRef.current = searchTerm;

      try {
        const results = await fetchRecentRevisions(searchParameters);
        if (thisRequestId !== requestsCounterRef.current) return;

        // Normal results flow
        if (results.length) {
          setRecentRevisions(results);

          // Auto select logic for hashes should run after results are set
          if (autoSelect && (isPartialHash.test(searchTerm) || isCompleteHash.test(searchTerm))) {
            const termLower = searchTerm.toLowerCase().trim();

            const match = results.find(
              (rev) =>
                rev.revision.toLowerCase() === termLower ||
                (isPartialHash.test(searchTerm) && rev.revision.toLowerCase().startsWith(termLower)),
            );

            if (match) {
              const alreadySelected = displayedRevisions.some(
                (rev) => rev.id === match.id,
              );

              if (!(searchType === 'new' && alreadySelected)) {
                onSearchResultsToggle(match);
                // Do NOT clear inputValue after auto-select, especially for complete hashes
                return;
              }
            }
          }
        } else {
          setSearchError('No results found');
          setRecentRevisions(null);
        }
      } catch (e) {
        console.error('Error while fetching recent revisions:', e);
        const strError =
          typeof e === 'string'
            ? e
            : e instanceof Error
              ? e.message
              : `Unknown error: ${String(e)}`;
        setSearchError(strError || 'An error has occurred');
        setRecentRevisions(null);
      }
    },
    [repository, displayedRevisions, searchType, onSearchResultsToggle],
  );

  const debouncedSearchRecentRevisions = useCallback(
    simpleDebounce(searchRecentRevisions),
    [searchRecentRevisions],
  );

  const onValueChange = (searchTerm: string) => {
    setInputValue(searchTerm);

    const isCompleteHash = /^(?=.*[a-f])[a-f0-9]{40}$/i;
    const isPartialHash = /^(?=.*[a-f])[a-f0-9]{4,39}$/i;

    setSearchError(null);
    setRecentRevisions(null);

    if (isCompleteHash.test(searchTerm)) {
      // For complete hashes, always attempt auto-select immediately
      void debouncedSearchRecentRevisions(searchTerm, { autoSelect: true });
    } else if (isPartialHash.test(searchTerm)) {
      // For partial hashes, also attempt auto-select
      void debouncedSearchRecentRevisions(searchTerm, { autoSelect: true });
    } else {
      debouncedSearchRecentRevisions(searchTerm);
    }
  };

  // At load time and everytime the repository information changes...
  useEffect(() => {
    void searchRecentRevisions(lastSearchTermRef.current);
  }, [repository, searchRecentRevisions]);

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <RevisionAutocompleteInput
      params={params}
      searchError={searchError}
      inputPlaceholder={inputPlaceholder}
      searchType={searchType}
      compact={compact}
    />
  );

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: Changeset,
  ) => (
    <RevisionAutocompleteOption
      {...props}
      key={option.id}
      index={recentRevisions?.indexOf(option) ?? 0}
      item={option}
      isChecked={displayedRevisions.map((rev) => rev.id).includes(option.id)}
      onToggle={onSearchResultsToggle}
      listItemComponent={listItemComponent}
    />
  );

  return (
    <Box ref={containerRef}>
      <Autocomplete
        options={recentRevisions ?? []}
        getOptionLabel={(options) => options.revision}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        multiple={listItemComponent !== 'radio'}
        disableCloseOnSelect={listItemComponent !== 'radio'}
        filterOptions={(options) => options}
        inputValue={inputValue} // Controlled input
        onInputChange={(_, value) => onValueChange(value)}
        onChange={(_, value, _reason, details) => {
          if (details?.option) {
            onSearchResultsToggle(details.option);
          }
        }}
        renderInput={renderInput}
        renderOption={renderOption}
        loading={recentRevisions?.length === 0}
        loadingText={'Loading...'}
        noOptionsText={searchError || 'No results found'}
        slotProps={{
          listbox: {
            className: `${getListStyles(mode)} results-list-${mode}`,
          },
        }}
        data-testid='autocomplete'
      />
    </Box>
  );
}
